import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import {
  isStrongPassword,
  MAX_PASSWORD_LENGTH,
  PASSWORD_REQUIREMENT_TEXT,
} from "@/lib/passwordPolicy";
import { GENDER_OPTIONS, type Gender } from "@/lib/gender";
import SettingsToggle from "@/components/SettingsToggle";
import BrandLogo from "@/components/BrandLogo";
import { PasswordRequirements } from "@/components/PasswordRequirements";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { isValidEmail, isValidPersonName, isValidPhone, normalizePersonName, sanitizePersonNameInput, PERSON_NAME_ERROR } from "@/lib/utils";
import { authReturnFromSearch, rememberAuthReturn, withAuthReturn } from "@/lib/authReturn";

export default function Signup() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [gender, setGender] = useState<Gender | "">("");
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const returnTo = authReturnFromSearch(window.location.search);
  const passwordIsStrong = isStrongPassword(password);

  const signupMutation = useMutation({
    mutationFn: async (data: { email: string; password: string; firstName?: string; lastName?: string; phone?: string; gender?: Gender }) => {
      const response = await apiRequest("POST", "/api/auth/register", data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Request received",
        description:
          "If this address can be registered, check its inbox for next steps. If you already have an account, sign in or reset your password.",
      });
      rememberAuthReturn(returnTo);
      setLocation(returnTo ? withAuthReturn("/login", returnTo) : "/login");
    },
    onError: (error) => {
      toast({
        title: "Registration Failed",
        description: error.message || "Please try again with different details.",
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({
        title: "Error",
        description: "Please enter both email and password",
        variant: "destructive",
      });
      return;
    }
    if (!isValidEmail(email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address (e.g. name@example.com).",
        variant: "destructive",
      });
      return;
    }
    if (!passwordIsStrong) {
      toast({
        title: "Choose a stronger password",
        description: PASSWORD_REQUIREMENT_TEXT,
        variant: "destructive",
      });
      return;
    }
    if (phone.trim() && !isValidPhone(phone)) {
      toast({
        title: "Check the phone number",
        description: "Enter a valid phone number (7–15 digits, + allowed) or leave it blank.",
        variant: "destructive",
      });
      return;
    }
    if (!isValidPersonName(firstName)) {
      toast({ title: "Invalid first name", description: PERSON_NAME_ERROR, variant: "destructive" });
      return;
    }
    if (lastName.trim() && !isValidPersonName(lastName)) {
      toast({ title: "Invalid last name", description: PERSON_NAME_ERROR, variant: "destructive" });
      return;
    }
    signupMutation.mutate({
      email: email.trim(),
      password,
      firstName: normalizePersonName(firstName),
      ...(lastName.trim() ? { lastName: normalizePersonName(lastName) } : {}),
      ...(phone.trim() ? { phone: phone.trim() } : {}),
      ...(gender ? { gender } : {}),
    });
  };

  return (
    <div className="min-h-screen cosmic-bg flex items-center justify-center py-4" data-testid="signup-page">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-3">
          <Link href={returnTo ? withAuthReturn("/login", returnTo) : "/login"}>
            <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full" aria-label="Back">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <SettingsToggle />
        </div>
        <div className="text-center mb-4">
          <div className="animate-float mb-3">
            <BrandLogo width={190} />
          </div>
          <p className="text-muted-foreground text-sm">
            Begin your cosmic journey today
          </p>
        </div>

        <Card className="cosmic-card border-0 shadow-lg overflow-hidden" data-testid="signup-form-card">
          <div className="h-1 bg-gradient-to-r from-cyan-500 via-violet-500 to-fuchsia-500" />
          <CardHeader className="pb-4">
            <CardTitle className="text-center text-lg font-medium text-foreground">
              Create Account
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName" className="block text-sm font-medium mb-2 text-foreground">
                    First Name
                  </Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(sanitizePersonNameInput(e.target.value))}
                    className="w-full px-4 py-3 bg-input border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent text-foreground"
                    placeholder="First name"
                    maxLength={100}
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="block text-sm font-medium mb-2 text-foreground">
                    Last Name
                  </Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(sanitizePersonNameInput(e.target.value))}
                    className="w-full px-4 py-3 bg-input border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent text-foreground"
                    placeholder="Last name"
                    maxLength={100}
                    data-testid="input-last-name"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone" className="block text-sm font-medium mb-2 text-foreground">
                  Phone Number <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-4 py-3 bg-input border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent text-foreground"
                  placeholder="+91 98765 43210"
                  maxLength={20}
                  data-testid="input-phone"
                />
              </div>

              <div>
                <Label htmlFor="gender" className="block text-sm font-medium mb-2 text-foreground">
                  Gender <span className="text-muted-foreground font-normal">(optional)</span>
                </Label>
                <select
                  id="gender"
                  value={gender}
                  onChange={(event) => setGender(event.target.value as Gender | "")}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  data-testid="select-gender"
                >
                  <option value="">Not specified</option>
                  {GENDER_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label htmlFor="email" className="block text-sm font-medium mb-2 text-foreground">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-input border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent text-foreground"
                  placeholder="Enter your email"
                  data-testid="input-email"
                  required
                />
              </div>

              <div>
                <Label htmlFor="password" className="block text-sm font-medium mb-2 text-foreground">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    maxLength={MAX_PASSWORD_LENGTH}
                    aria-describedby="password-requirements"
                    aria-invalid={password.length > 0 && !passwordIsStrong}
                    className={`w-full px-4 py-4 pr-12 bg-input border rounded-lg focus:ring-2 focus:border-transparent text-foreground text-xl font-medium tracking-[0.25em] placeholder:text-base placeholder:font-normal placeholder:tracking-normal ${
                      password.length > 0 && !passwordIsStrong
                        ? "border-destructive focus:ring-destructive"
                        : "border-border focus:ring-ring"
                    }`}
                    placeholder="Create a strong password"
                    data-testid="input-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    title={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-white/5 transition"
                    data-testid="button-toggle-password"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <div className="mt-2">
                  <PasswordRequirements password={password} />
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={signupMutation.isPending}
                className="w-full px-6 py-3 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600 hover:from-violet-500 hover:via-fuchsia-500 hover:to-pink-500 text-white rounded-lg font-semibold transition-all animate-glow"
                data-testid="button-signup"
              >
                {signupMutation.isPending ? (
                  <span className="flex items-center">
                    <i className="fas fa-spinner fa-spin mr-2" aria-hidden="true"></i>
                    Creating Account...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <i className="fas fa-user-plus mr-2" aria-hidden="true"></i>
                    Create Account
                  </span>
                )}
              </Button>
            </form>

            <div className="text-center mt-6">
              <p className="text-muted-foreground">
                Already have an account?{" "}
                <Link href="/login">
                  <span className="text-accent hover:text-accent/80 font-medium cursor-pointer" data-testid="link-login">
                    Sign in here
                  </span>
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
